import React, { useCallback, createRef } from 'react';
import { Paper, Button, makeStyles, Theme, FormControl, FormLabel, FormHelperText, TextField } from '@material-ui/core';
import clsx from 'clsx';
import { usePropValue, inputStateSetterHelper, useModePanelStyle, useModeFormStyle, FontIcon, BaseEntityField, BaseInfoCompProps,
    BaseInfoCompActionsSet, BaseInfoCompSaveAction, InfoCompPanelHeader, ModeInfoCompSelectInputField } from '@moderepo/mode-app-components';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';


const useStyle = makeStyles((theme: Theme) => {
    return {
        formControl: {
            marginBottom: theme.spacing(2),
            width       : '100%',
        },

        formControlLabel: {
            paddingBottom: theme.spacing(1),
        },

        formGroup: {
            paddingLeft: theme.spacing(2),
        },

        datesSeparator: {
            margin: theme.spacing(0, 1),
        },

        fileInputField: {
            display: 'none',
        },
    };
}, { name: 'RCCreateFileRequest' });



/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface RCCreateFileRequestInputs {
    readonly fileType?: string | undefined;
    readonly startDate?: number | undefined;
    readonly endDate?: number | undefined;
    readonly uploadFile?: File | undefined;
}


/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface RCCreateFileRequestActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<RCCreateFileRequestInputs>
}


export interface RCCreateFileRequestProps extends BaseInfoCompProps {

    readonly fields: {
        readonly fileType?: BaseEntityField<string> | undefined;
        readonly startDate?: BaseEntityField<number> | undefined;
        readonly endDate?: BaseEntityField<number> | undefined;
        readonly uploadFile?: BaseEntityField<File> | undefined;
    };

    // Override the BaseInfoCompProps actions set with the RCCreateFileRequestActionsSet
    readonly actions: RCCreateFileRequestActionsSet;


    readonly minDate: number;
    readonly maxDate: number;

    readonly texts: {
        readonly dateInputLabel: string;
        readonly dateFormat: string;
        readonly todayLabel: string;
        readonly okLabel: string;
        readonly cancelLabel: string;
    }
}



/**
 * This is a DUMB component used for displaying create File Request form. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCCreateFileRequest: React.FC<RCCreateFileRequestProps> = (props: RCCreateFileRequestProps) => {
    const [fileTypeInput, setFileTypeInput] = usePropValue<string | undefined>(props.fields.fileType?.value);
    const [startDateInput, setStartDateInput] = usePropValue<number | undefined>(props.fields.startDate?.value);
    const [endDateInput, setEndDateInput] = usePropValue<number | undefined>(props.fields.endDate?.value);
    const [uploadFileInput, setUploadFileInput] = usePropValue<File | undefined>(undefined);
    const fileInputRef = createRef<HTMLInputElement>();
    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();



    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {
        inputStateSetterHelper(inputName, value, {
            fileType  : setFileTypeInput,
            startDate : setStartDateInput,
            endDate   : setEndDateInput,
            uploadFile: setUploadFileInput,
        });
    }, [setFileTypeInput, setStartDateInput, setEndDateInput, setUploadFileInput]);



    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setFileTypeInput(props.fields.fileType?.value);
        setStartDateInput(props.fields.startDate?.value);
        setEndDateInput(props.fields.endDate?.value);
        setUploadFileInput(undefined);
    }, [setFileTypeInput, props.fields.fileType, props.fields.startDate, props.fields.endDate, setStartDateInput, setEndDateInput,
        setUploadFileInput]);



    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                fileType  : fileTypeInput,
                startDate : startDateInput,
                endDate   : endDateInput,
                uploadFile: uploadFileInput,
            });
        }

        return false;
    }, [props.actions, fileTypeInput, startDateInput, endDateInput, uploadFileInput]);



    /**
     * On file selected, we will call 'onInputChange' to handle updating the state
     */
    const onFileInputChange = useCallback((event: React.FormEvent<HTMLInputElement>) => {
        // Get the file the user selected and update the state
        const target = event.target as HTMLInputElement;
        if (target.files?.length === 1) {
            const selectedFile = target.files[0];
            onInputChange('uploadFile', selectedFile);
            if (props.onFormInputChange) {
                props.onFormInputChange('uploadFile', selectedFile);
            }
        }
    }, [onInputChange, props]);



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    {props.fields.fileType && (
                        <ModeInfoCompSelectInputField
                            className={formClasses.formInputField}
                            compProps={props}
                            inputProps={props.fields.fileType}
                            fieldName="fileType"
                            inputValue={fileTypeInput}
                            onInputChange={onInputChange}
                        />
                    )}
                    {props.fields.startDate && props.fields.endDate && (
                        <FormControl
                            required
                            className={formClasses.formInputField}
                            error={Boolean(props.fields.startDate.error || props.fields.endDate.error)}
                        >
                            <FormLabel
                                className={classes.formControlLabel}
                            >{props.texts.dateInputLabel}
                            </FormLabel>
                            <div className={clsx(formClasses.formInputGroup, 'xx-small')}>
                                <KeyboardDateTimePicker
                                    label={props.fields.startDate.label}
                                    allowKeyboardControl
                                    minDate={props.minDate}
                                    maxDate={endDateInput}
                                    value={startDateInput}
                                    format={props.texts.dateFormat}
                                    ampm={false}
                                    todayLabel={props.texts.todayLabel}
                                    okLabel={props.texts.okLabel}
                                    cancelLabel={props.texts.cancelLabel}
                                    showTodayButton
                                    disableFuture
                                    onChange={(value: MaterialUiPickersDate) => {
                                        if (value) {
                                            onInputChange('startDate', value.valueOf());
                                            if (props.onFormInputChange) {
                                                props.onFormInputChange('startDate', value?.toDate());
                                            }
                                        }
                                    }}
                                />
                                <KeyboardDateTimePicker
                                    label={props.fields.endDate.label}
                                    allowKeyboardControl
                                    minDate={startDateInput}
                                    maxDate={props.maxDate}
                                    value={endDateInput}
                                    format={props.texts.dateFormat}
                                    ampm={false}
                                    todayLabel={props.texts.todayLabel}
                                    okLabel={props.texts.okLabel}
                                    cancelLabel={props.texts.cancelLabel}
                                    showTodayButton
                                    disableFuture
                                    onChange={(value: MaterialUiPickersDate) => {
                                        if (value) {
                                            onInputChange('endDate', value.valueOf());
                                            if (props.onFormInputChange) {
                                                props.onFormInputChange('endDate', value.toDate());
                                            }
                                        }
                                    }}
                                />
                            </div>
                            <FormHelperText>{props.fields.startDate.error || props.fields.endDate.error}</FormHelperText>
                        </FormControl>
                    )}

                    {props.fields.uploadFile && !props.fields.uploadFile.hidden && (!props.fields.uploadFile.editingModeOnly || props.isEditing)
                        && !(props.fields.uploadFile.hideOnEdit && props.isEditing) && (
                        <>
                            <TextField
                                key={`${props.fields.uploadFile.label}-${props.fields.uploadFile.value || ''}-
                                    ${props.fields.uploadFile.displayValue || ''}`}
                                variant={props.fields.uploadFile.variant || 'standard'}
                                className={clsx(`${formClasses.formInputField}`,
                                    !props.fields.uploadFile.editable ? 'readonly' : '',
                                    !props.isEditing && props.fields.uploadFile.clickable && 'clickable',
                                    props.isEditing && !props.fields.uploadFile.editable ? 'fade' : '')}
                                type="text"
                                label={props.fields.uploadFile.label}
                                InputLabelProps={{
                                    shrink   : true,
                                    className: props.fields.uploadFile.editable ? 'editable-label-icon' : '',
                                }}
                                InputProps={{
                                    className : clsx('input-wrapper'),
                                    inputProps: props.fields.uploadFile.editable
                                        ? {
                                            placeholder: props.fields.uploadFile.placeholder,
                                            readOnly   : true,
                                        }
                                        : {
                                            readOnly: true,
                                        },
                                }}
                                required={props.isEditing && props.fields.uploadFile.required}
                                error={Boolean(props.fields.uploadFile.editable && props.fields.uploadFile.error)}
                                helperText={props.fields.uploadFile.editable
                                    ? props.fields.uploadFile.error || props.fields.uploadFile.description : undefined}
                                value={uploadFileInput?.name || ''}

                                onChange={(event: React.FormEvent) => {
                                    if (props.fields.uploadFile?.editable) {
                                        if (props.onFormInputChange) {
                                            props.onFormInputChange('uploadFile', (event.currentTarget as HTMLInputElement).value);
                                        }
                                        if (onInputChange) {
                                            onInputChange('uploadFile', (event.currentTarget as HTMLInputElement).value);
                                        }
                                    }
                                }}

                                onClick={() => {
                                    // If field is editable and we are not in editing mode, fire input clicked even to let the container know
                                    if (props.fields.uploadFile && (props.fields.uploadFile.editable || props.fields.uploadFile.clickable)
                                        && props.onFieldClicked && !props.isEditing) {
                                        props.onFieldClicked('uploadFile');
                                    }
                                    if (fileInputRef.current?.click) {
                                        fileInputRef.current.click();
                                    }
                                }}
                            />
                            <input ref={fileInputRef} className={classes.fileInputField} type="file" onChange={onFileInputChange} />
                        </>
                    )}


                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>

        </Paper>
    );

};
